import { useState } from "react";
import type { MatrixClient, Room } from "matrix-js-sdk";
import { Avatar, Button, Text } from "../../theme/primitives";
import { formatLastActive } from "../../lib/time";
import type { PresenceTypingState } from "./usePresenceTyping";
import { ProfilePanel } from "./ProfilePanel";
import { GroupInfoPanel } from "./GroupInfoPanel";

interface ChatHeaderProps {
  client: MatrixClient;
  room: Room;
  title: string;
  avatarUrl: string | null;
  isGroup: boolean;
  memberCount: number;
  otherUserId: string | null;
  presenceTyping: PresenceTypingState;
  onBack?: () => void;
}

function statusLine(pt: PresenceTypingState): { text: string; online: boolean } {
  if (pt.otherTyping) return { text: "typing…", online: true };
  const p = pt.presence;
  if (!p) return { text: "", online: false };
  if (p.presence === "online") return { text: "online", online: true };
  if (p.lastActiveAgoMs != null) return { text: formatLastActive(p.lastActiveAgoMs), online: false };
  return { text: "offline", online: false };
}

export function ChatHeader({
  client,
  room,
  title,
  avatarUrl,
  isGroup,
  memberCount,
  otherUserId,
  presenceTyping,
  onBack,
}: ChatHeaderProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const status = statusLine(presenceTyping);
  const subtitle = isGroup ? `${memberCount} members` : status.text;
  const canOpenInfo = isGroup || otherUserId !== null;

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-md)",
        padding: "var(--space-sm) var(--space-lg)",
        background: "var(--color-header-bg)",
        color: "var(--color-header-text)",
        borderBottom: "1px solid var(--color-header-bg)",
      }}
    >
      {onBack && (
        <Button
          variant="icon"
          onClick={onBack}
          aria-label="Back"
          style={{ width: 32, height: 32, color: "var(--color-header-text)" }}
        >
          ←
        </Button>
      )}
      <button
        type="button"
        onClick={() => canOpenInfo && setInfoOpen(true)}
        aria-label="View info"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-md)",
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          padding: 0,
          textAlign: "left",
          cursor: canOpenInfo ? "pointer" : "default",
          color: "inherit",
        }}
      >
        <Avatar name={title} src={avatarUrl} size={40} online={isGroup ? undefined : status.online} />
        <div style={{ minWidth: 0 }}>
          <Text weight="medium" truncate style={{ color: "var(--color-header-text)" }}>
            {title}
          </Text>
          <div>
            <Text size="sm" style={{ color: "var(--color-header-muted)" }}>
              {subtitle}
            </Text>
          </div>
        </div>
      </button>

      {infoOpen && isGroup && (
        <GroupInfoPanel client={client} room={room} onClose={() => setInfoOpen(false)} />
      )}
      {infoOpen && !isGroup && otherUserId && (
        <ProfilePanel
          client={client}
          userId={otherUserId}
          fallbackName={title}
          avatarUrl={avatarUrl}
          status={status}
          onClose={() => setInfoOpen(false)}
        />
      )}
    </header>
  );
}
